import { Pipe, PipeTransform } from '@angular/core';

/**
 * Formats an EDIFACT datetime string (YYYYMMDDHHmm or YYYYMMDDHHmm-YYYYMMDDHHmm)
 * into a human-readable label.
 *
 * Examples:
 *   '202604150200-202604150230' → '15-Apr-2026 02:00 to 02:30'
 *   '202605120000'             → '12-May-2026 00:00'
 */
@Pipe({ name: 'edifactDatetime' })
export class EdifactDatetimePipe implements PipeTransform {

  private readonly MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  transform(value: string | null | undefined): string {
    if (!value || value.trim() === '') {
      return '';
    }

    const parts = value.trim().split('-');

    if (parts.length === 2 && parts[0].length === 12 && parts[1].length === 12) {
      const start = this.parse(parts[0]);
      const end = this.parse(parts[1]);
      if (!start || !end) {
        return value;
      }

      const startDate = this.formatDate(start);
      const startTime = this.formatTime(start);
      const endDate = this.formatDate(end);
      const endTime = this.formatTime(end);

      if (startDate === endDate) {
        // Same day — show date once: '15-Apr-2026 02:00 to 02:30'
        return `${startDate} ${startTime} to ${endTime}`;
      }
      // Different days — show both dates: '15-Apr-2026 02:00 to 16-Apr-2026 02:30'
      return `${startDate} ${startTime} to ${endDate} ${endTime}`;
    }

    if (parts.length === 1 && parts[0].length === 12) {
      const dt = this.parse(parts[0]);
      if (!dt) {
        return value;
      }
      return `${this.formatDate(dt)} ${this.formatTime(dt)}`;
    }

    // Unrecognised format — return as-is
    return value;
  }

  private parse(raw: string): { year: number; month: number; day: number; hour: number; minute: number } | null {
    if (raw.length !== 12 || !/^\d{12}$/.test(raw)) {
      return null;
    }
    const year   = parseInt(raw.substring(0, 4), 10);
    const month  = parseInt(raw.substring(4, 6), 10);
    const day    = parseInt(raw.substring(6, 8), 10);
    const hour   = parseInt(raw.substring(8, 10), 10);
    const minute = parseInt(raw.substring(10, 12), 10);
    if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) {
      return null;
    }
    return { year, month, day, hour, minute };
  }

  private formatDate(dt: { year: number; month: number; day: number }): string {
    const d = String(dt.day).padStart(2, '0');
    const m = this.MONTH_NAMES[dt.month - 1];
    return `${d}-${m}-${dt.year}`;
  }

  private formatTime(dt: { hour: number; minute: number }): string {
    const h = String(dt.hour).padStart(2, '0');
    const m = String(dt.minute).padStart(2, '0');
    return `${h}:${m}`;
  }
}
